/**
 * UIContext - UI State Management
 *
 * Manages UI-specific state like modals, navigation, mobile menu, dark mode.
 * Provides centralized UI state across the app.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const UIContext = createContext();
const ACTIVE_THEME_SCOPE_KEY = 'thryveloop_active_theme_scope_v1';
const THEME_SCOPE_STORAGE_KEYS = {
  marketing: 'thryveloop_theme_preference_marketing_v1',
  app: 'thryveloop_theme_preference_app_v1'
};
const THEME_SCOPE_DEFAULTS = {
  marketing: 'light',
  app: 'light'
};
const MOBILE_NAV_BREAKPOINT = 900;

const normalizeThemeScope = (scope) => (scope === 'app' ? 'app' : 'marketing');
const normalizeThemePreference = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return normalized === 'dark' || normalized === 'light' || normalized === 'system' ? normalized : '';
};

const getSystemDarkMode = () => window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;

const resolveDarkModeFromPreference = (preference) => {
  if (preference === 'dark') return true;
  if (preference === 'light') return false;
  return getSystemDarkMode();
};

const readScopedThemePreference = (scope) => {
  const normalizedScope = normalizeThemeScope(scope);
  const scopedKey = THEME_SCOPE_STORAGE_KEYS[normalizedScope];
  const scopedPreference = normalizeThemePreference(localStorage.getItem(scopedKey));
  if (scopedPreference) return scopedPreference;

  if (normalizedScope === 'app') {
    const hasMarketingScopePreference = Boolean(normalizeThemePreference(localStorage.getItem(THEME_SCOPE_STORAGE_KEYS.marketing)));
    if (!hasMarketingScopePreference) {
      const legacyPreference = normalizeThemePreference(localStorage.getItem('thryveloop_theme_preference'));
      if (legacyPreference) return legacyPreference;

      const legacyTheme = normalizeThemePreference(localStorage.getItem('thryveloop-theme'));
      if (legacyTheme) return legacyTheme;

      const storedDarkMode = localStorage.getItem('thryveloop_darkMode');
      if (storedDarkMode === 'true') return 'dark';
      if (storedDarkMode === 'false') return 'light';
    }
  }

  return THEME_SCOPE_DEFAULTS[normalizedScope];
};

const persistActiveThemePreference = (preference, darkMode) => {
  localStorage.setItem('thryveloop_theme_preference', preference);
  localStorage.setItem('thryveloop_darkMode', String(darkMode));
  localStorage.setItem('thryveloop-theme', darkMode ? 'dark' : 'light');
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export const UIProvider = ({ children }) => {
  const [themeScope, setThemeScopeState] = useState(() => {
    try {
      return normalizeThemeScope(localStorage.getItem(ACTIVE_THEME_SCOPE_KEY));
    } catch (_) {
      return 'marketing';
    }
  });

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const activeScope = normalizeThemeScope(localStorage.getItem(ACTIVE_THEME_SCOPE_KEY));
      return resolveDarkModeFromPreference(readScopedThemePreference(activeScope));
    } catch (_) {
      // ignore localStorage access errors
    }

    return resolveDarkModeFromPreference(THEME_SCOPE_DEFAULTS.marketing);
  });

  // Navigation
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Email collection modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailFeature, setEmailFeature] = useState(null);
  const [emailFeatureName, setEmailFeatureName] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  // Ollama setup wizard (desktop app only)
  const [showOllamaWizard, setShowOllamaWizard] = useState(false);

  // User dropdown
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Editing states
  const [editingObservation, setEditingObservation] = useState(null);
  const [editingExperiment, setEditingExperiment] = useState(null);

  // Change password modal
  const [showChangePassword, setShowChangePassword] = useState(false);

  /**
   * Persist dark mode to localStorage
   */
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_THEME_SCOPE_KEY, themeScope);
      const activePreference = readScopedThemePreference(themeScope);
      persistActiveThemePreference(activePreference, darkMode);
    } catch (_) {
      // ignore localStorage access errors
    }
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode, themeScope]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let activePreference = '';
    try {
      activePreference = readScopedThemePreference(themeScope);
    } catch (_) {
      activePreference = '';
    }
    if (activePreference !== 'system') {
      return undefined;
    }

    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      return undefined;
    }

    const handleThemeChange = (event) => {
      setDarkMode(event.matches);
      try {
        persistActiveThemePreference('system', event.matches);
      } catch (_) {
        // ignore localStorage write errors
      }
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleThemeChange);
      return () => mediaQuery.removeEventListener('change', handleThemeChange);
    }

    mediaQuery.addListener(handleThemeChange);
    return () => mediaQuery.removeListener(handleThemeChange);
  }, [themeScope]);

  const setThemePreferenceForScope = useCallback((scope, preference) => {
    if (typeof window === 'undefined') return;
    const normalizedScope = normalizeThemeScope(scope);
    const normalizedPreference = normalizeThemePreference(preference) || THEME_SCOPE_DEFAULTS[normalizedScope];
    const nextDarkMode = resolveDarkModeFromPreference(normalizedPreference);
    let isActiveScope = themeScope === normalizedScope;

    try {
      localStorage.setItem(THEME_SCOPE_STORAGE_KEYS[normalizedScope], normalizedPreference);
      isActiveScope = isActiveScope || normalizeThemeScope(localStorage.getItem(ACTIVE_THEME_SCOPE_KEY)) === normalizedScope;
      if (isActiveScope) {
        persistActiveThemePreference(normalizedPreference, nextDarkMode);
      }
    } catch (_) {
      // ignore localStorage access errors
    }

    if (isActiveScope) {
      setDarkMode(nextDarkMode);
    }
  }, [themeScope]);

  const setThemeScope = useCallback((scope) => {
    const normalizedScope = normalizeThemeScope(scope);
    const nextPreference = (() => {
      try {
        return readScopedThemePreference(normalizedScope);
      } catch (_) {
        return THEME_SCOPE_DEFAULTS[normalizedScope];
      }
    })();
    const nextDarkMode = resolveDarkModeFromPreference(nextPreference);

    setThemeScopeState((prev) => (prev === normalizedScope ? prev : normalizedScope));
    setDarkMode((prev) => (prev === nextDarkMode ? prev : nextDarkMode));

    try {
      localStorage.setItem(THEME_SCOPE_STORAGE_KEYS[normalizedScope], nextPreference);
      localStorage.setItem(ACTIVE_THEME_SCOPE_KEY, normalizedScope);
      persistActiveThemePreference(nextPreference, nextDarkMode);
    } catch (_) {
      // ignore localStorage access errors
    }
  }, []);

  /**
   * Toggle dark mode
   */
  const toggleDarkMode = useCallback(() => {
    const nextPreference = darkMode ? 'light' : 'dark';
    const nextDarkMode = nextPreference === 'dark';
    setDarkMode(nextDarkMode);
    try {
      localStorage.setItem(THEME_SCOPE_STORAGE_KEYS[themeScope], nextPreference);
      persistActiveThemePreference(nextPreference, nextDarkMode);
    } catch (_) {
      // ignore localStorage write errors
    }
    setThemePreferenceForScope(themeScope, nextPreference);
  }, [darkMode, setThemePreferenceForScope, themeScope]);

  /**
   * Navigate to a tab
   */
  const navigateTo = useCallback((tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  }, []);

  /**
   * Toggle mobile menu
   */
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  /**
   * Close mobile menu
   */
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  /**
   * Keep the mobile drawer from leaving the page scrollable underneath and
   * close it when the viewport exits the mobile breakpoint.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      if (window.innerWidth > MOBILE_NAV_BREAKPOINT) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    if (window.innerWidth > MOBILE_NAV_BREAKPOINT) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;
    const previousLeft = document.body.style.left;
    const previousRight = document.body.style.right;
    const storedScrollY = Number(document.body.dataset.mobileMenuScrollY || '0') || 0;

    if (isMobileMenuOpen) {
      const lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.dataset.mobileMenuScrollY = String(lockedScrollY);
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      if (storedScrollY > 0) {
        window.scrollTo({ top: storedScrollY, left: 0, behavior: 'auto' });
      }
      delete document.body.dataset.mobileMenuScrollY;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      document.body.style.left = previousLeft;
      document.body.style.right = previousRight;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (window.innerWidth > MOBILE_NAV_BREAKPOINT || isMobileMenuOpen) {
      return;
    }

    const resetHorizontalOffset = (node) => {
      if (!node || typeof node.scrollTo !== 'function') return;
      try {
        node.scrollTo({ left: 0, behavior: 'auto' });
      } catch (_) {
        node.scrollLeft = 0;
      }
    };

    window.requestAnimationFrame(() => {
      window.scrollTo({ left: 0, top: window.scrollY, behavior: 'auto' });
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
      document.querySelectorAll('.panel-container, .main-content, .analytics-view, .dashboard-view').forEach(resetHorizontalOffset);
    });
  }, [isMobileMenuOpen]);

  /**
   * Open email collection modal
   */
  const openEmailModal = useCallback((feature, featureName, action = null) => {
    setEmailFeature(feature);
    setEmailFeatureName(featureName);
    setPendingAction(action);
    setShowEmailModal(true);
  }, []);

  /**
   * Close email collection modal
   */
  const closeEmailModal = useCallback(() => {
    setShowEmailModal(false);
    setEmailFeature(null);
    setEmailFeatureName('');
    setPendingAction(null);
  }, []);

  /**
   * Open Ollama wizard
   */
  const openOllamaWizard = useCallback(() => {
    setShowOllamaWizard(true);
  }, []);

  /**
   * Close Ollama wizard
   */
  const closeOllamaWizard = useCallback(() => {
    setShowOllamaWizard(false);
  }, []);

  /**
   * Toggle user dropdown
   */
  const toggleUserDropdown = useCallback(() => {
    setShowUserDropdown(prev => !prev);
  }, []);

  /**
   * Close user dropdown
   */
  const closeUserDropdown = useCallback(() => {
    setShowUserDropdown(false);
  }, []);

  /**
   * Start editing an observation
   */
  const startEditObservation = useCallback((observation) => {
    setEditingObservation(observation);
    navigateTo('add-observations');
  }, [navigateTo]);

  /**
   * Cancel editing observation
   */
  const cancelEditObservation = useCallback(() => {
    setEditingObservation(null);
  }, []);

  /**
   * Start editing an experiment
   */
  const startEditExperiment = useCallback((experiment) => {
    setEditingExperiment(experiment);
    navigateTo('add-experiments');
  }, [navigateTo]);

  /**
   * Cancel editing experiment
   */
  const cancelEditExperiment = useCallback(() => {
    setEditingExperiment(null);
  }, []);

  /**
   * Open change password modal
   */
  const openChangePassword = useCallback(() => {
    setShowChangePassword(true);
    closeUserDropdown();
  }, [closeUserDropdown]);

  /**
   * Close change password modal
   */
  const closeChangePassword = useCallback(() => {
    setShowChangePassword(false);
  }, []);

  /**
   * Close all overlays/modals
   */
  const closeAllOverlays = useCallback(() => {
    setIsMobileMenuOpen(false);
    setShowUserDropdown(false);
    setShowEmailModal(false);
    setShowOllamaWizard(false);
    setShowChangePassword(false);
  }, []);

  const value = {
    // Dark mode
    darkMode,
    setDarkMode,
    toggleDarkMode,
    themeScope,
    setThemeScope,
    setThemePreferenceForScope,

    // Navigation
    activeTab,
    setActiveTab,
    navigateTo,

    // Mobile menu
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    toggleMobileMenu,
    closeMobileMenu,

    // Email modal
    showEmailModal,
    emailFeature,
    emailFeatureName,
    pendingAction,
    openEmailModal,
    closeEmailModal,
    setPendingAction,

    // Ollama wizard
    showOllamaWizard,
    openOllamaWizard,
    closeOllamaWizard,

    // User dropdown
    showUserDropdown,
    toggleUserDropdown,
    closeUserDropdown,

    // Editing states
    editingObservation,
    editingExperiment,
    startEditObservation,
    cancelEditObservation,
    startEditExperiment,
    cancelEditExperiment,

    // Change password
    showChangePassword,
    openChangePassword,
    closeChangePassword,

    // Utilities
    closeAllOverlays,
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

UIProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UIContext;
